import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import api, { setToken, clearToken } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz && data?.timezone !== tz) api.post("/profile/timezone", { timezone: tz }).catch(() => {});
      return data;
    } catch (e) {
      clearToken();
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth callback, let AuthCallback handle it.
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token); setUser(data.user); return data.user;
  }, []);
  const register = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    setToken(data.token); setUser(data.user); return data.user;
  }, []);
  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    clearToken(); setUser(null);
  }, []);

  const value = useMemo(() => ({ user, setUser, loading, refresh, login, register, logout }), [user, loading, refresh, login, register, logout]);
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
